import { SubSink } from 'subsink';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  Input,
} from '@angular/core';
import { trigger, useAnimation, transition } from '@angular/animations';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AngularFirestore } from '@angular/fire/firestore';

import { AuthService, AuthUser } from '@monorepo/ui/auth';
import { fadeInAnimation } from '@monorepo/ui';

import { ChatBotService } from '../../services/chat-bot.service';

import { ChatBotMessage, ChatBotModel } from '../../models';

enum ChatFeedBackScreen {
  CHAT_FEEDBACK,
  GOOD_FEEDBACK,
  BAD_FEEDBACK,
}

@Component({
  selector: 'monorepo-user-chat-panel',
  templateUrl: './user-chat-panel.component.html',
  styleUrls: ['./user-chat-panel.component.scss'],
  animations: [
    trigger('fadeIn', [transition(':enter', useAnimation(fadeInAnimation))]),
  ],
})
export class UserChatPanelComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
  defaultPhotoUrl: string =
    'https://fomantic-ui.com/images/avatar/small/matt.jpg';
  defaulUserName: string = 'Guest';
  readonly good: string = 'Good';
  readonly bad: string = 'Bad';
  private subs = new SubSink();
  @Input() themeClass: any;
  public innerHeight: any;
  chatForm: FormGroup;
  chatBot: ChatBotModel;
  loggedUser: AuthUser;
  admins: AuthUser[] = [];
  primary: string;
  userId: string;
  isChatEnd: boolean = false;
  isShowEmoji: boolean = false;
  isChatExists: boolean = false;
  isUserExists: boolean = false;
  isShowChatbot: boolean = false;
  chatFeedBackScreen = ChatFeedBackScreen;
  feedBackResponse = ChatFeedBackScreen.CHAT_FEEDBACK;
  chatBotMessageList: ChatBotMessage[] = [];
  chatBotPayLoad: ChatBotModel = new ChatBotModel();
  chatBotMessage: ChatBotMessage = new ChatBotMessage();
  executionTime: number = 500;

  constructor(
    private authService: AuthService,
    private chatBotService: ChatBotService,
    private formBuilder: FormBuilder,
    private elementRef: ElementRef,
    private afs: AngularFirestore
  ) {}

  ngOnInit(): void {
    this.setThemeClass(this.themeClass);
    this.getAllAdmin();
    this.createInput();
    this.getAuthUser();
    this.setInnerHeight();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public setInnerHeight(): void {
    this.innerHeight = window.innerHeight;
    this.elementRef.nativeElement.style.setProperty(
      '--innerHeight',
      `${innerHeight}px`
    );
  }

  private scroll(): void {
    setTimeout(() => {
      let scrollIndex = this.viewPort.elementRef.nativeElement;
      this.viewPort.elementRef.nativeElement.scrollTo({
        top: scrollIndex.scrollHeight + scrollIndex.scrollTop,
        left: 0,
        behavior: 'auto',
      });
    }, this.executionTime);
  }

  private createInput(): void {
    this.chatForm = this.formBuilder.group({
      message: [''],
    });
  }

  public getAuthUser(): void {
    this.subs.sink = this.authService.user.subscribe({
      next: (user: AuthUser): void => {
        if (user) {
          this.loggedUser = user;
          this.getChat();
        } else {
          this.getChat();
        }
      },
    });
  }

  public getAllAdmin(): void {
    this.subs.sink = this.chatBotService.getAllAdminUsers().subscribe({
      next: (admins: AuthUser[]): void => {
        this.admins = admins;
      },
    });
  }

  public getChat(): void {
    if (this.loggedUser) {
      if (this.loggedUser.uid !== this.chatBotService.getChatSession()) {
        this.setLoggedUserSession();
      } else {
        this.userId = this.loggedUser.uid;
      }
    } else {
      this.userId = this.chatBotService.getChatSession();
    }
    this.getChatBot();
  }

  public setLoggedUserSession(): void {
    this.chatBotService.setChatSession(this.loggedUser.uid);
    if (this.chatBot) {
      this.chatBot.messages = [];
    }
    this.userId = this.loggedUser.uid;
  }

  public getChatBot(): void {
    if (this.userId) {
      this.subs.sink = this.chatBotService.getChatbot(this.userId).subscribe({
        next: (chatbot: ChatBotModel): void => {
          this.chatBot = chatbot;
          if (this.isChatExists && this.isChatEnd === false) {
            this.scroll();
          }
        },
      });
    }
  }

  public startConversation(): void {
    this.isChatExists = true;
    this.scroll();
    if (this.chatBot) {
      return;
    }
    this.setChatBotValue();
    if (this.loggedUser) {
      this.chatBotPayLoad.isAnonymous = false;
      this.createChatRoom();
    } else {
      this.createChatRoom();
    }
  }

  private setChatBotValue(): void {
    this.chatBotPayLoad.displayName = this.loggedUser
      ? this.loggedUser.displayName
      : this.defaulUserName;
    this.chatBotPayLoad.photoURL = this.loggedUser
      ? this.loggedUser.photoURL
      : this.defaultPhotoUrl;
    this.chatBotPayLoad.userId = this.loggedUser
      ? this.loggedUser.uid
      : this.afs.createId();
  }

  public createChatRoom(): void {
    this.subs.sink = this.chatBotService
      .createChatRoom(this.chatBotPayLoad)
      .subscribe({
        next: (): void => {
          this.chatBotService.setChatSession(this.chatBotPayLoad.userId);
          this.getChat();
        },
      });
  }

  public sendMessage(): void {
    this.isShowEmoji = false;
    this.scroll();
    if (!this.chatForm.get('message').value) {
      return;
    }

    this.chatBotMessage.senderId = this.chatBot.userId;
    this.chatBotMessage.displayName = this.chatBot.displayName;
    this.chatBotMessage.photoURL = this.chatBot.photoURL;
    this.chatBotMessage.message = this.chatForm.get('message').value;
    this.chatForm.reset();
    this.chatBotService.sendMessage(this.chatBot.id, this.chatBotMessage);
  }

  public setThemeClass(themeClass: any): void {
    this.elementRef.nativeElement.style.setProperty(
      '--primary',
      themeClass.primary
    );
    this.elementRef.nativeElement.style.setProperty(
      '--secondary',
      themeClass.secondary
    );
    this.elementRef.nativeElement.style.setProperty(
      '--comment',
      themeClass.comment
    );
  }

  public takeFeedBack(feedbackAction: number, feedback: string): void {
    this.feedBackResponse = feedbackAction;
    if (this.chatBot) {
      this.chatBotService.updateChatRoom(this.chatBot.id, feedback);
    }
  }

  public toggle(): void {
    this.isShowChatbot = !this.isShowChatbot;
    this.isChatEnd = false;
    this.isChatExists = false;
    this.feedBackResponse = this.chatFeedBackScreen.CHAT_FEEDBACK;
  }

  public showEmoji(event: boolean): void {
    this.isShowEmoji = event;
  }

  public endChat(): void {
    this.isChatEnd = true;
  }

  public selctedEmoji(event: any): void {
    let message = '';
    if (this.chatForm.get('message').value) {
      message += this.chatForm.get('message').value;
    }
    message += event.emoji.native;
    this.chatForm.get('message').setValue(message);
  }
}
